/**
 * Main Application Component
 *
 * Sets up routing and authentication context for the application.
 * Includes mobile platform support via Capacitor.
 *
 * Platform-specific routing:
 * - Wix: WixDashboardPage (Blog Writer only, Wix billing)
 * - iOS/Android/Web: SuiteDashboardPage (Full AI Suite, Stripe billing)
 *
 * Routes:
 * - / : Landing page (public, web only)
 * - /login : Login page (public)
 * - /signup : Signup page (public)
 * - /forgot-password : Password reset page (public)
 * - /dashboard : Main app (protected) - Routes to Suite or Wix based on context
 * - /wix-dashboard : Wix embedded experience (Wix only)
 * - /suite : Full AI Suite experience (iOS/Android/Web)
 */

import { useEffect, useMemo } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import MfaChallengeGuard from './components/auth/MfaChallengeGuard';
import ErrorBoundary from './components/ErrorBoundary';
import { Platform } from './utils/platform';
import { getWixInstanceFromUrl } from './utils/wixInstance';
import { useMfaChallenge } from './hooks/useMfaChallenge';

// Capacitor plugins
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Keyboard } from '@capacitor/keyboard';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import PricingPage from './pages/PricingPage';
import DashboardPage from './pages/DashboardPage';
import WixDashboardPage from './pages/WixDashboardPage';
import SuiteDashboardPage from './pages/SuiteDashboardPage';
import SettingsPage from './pages/SettingsPage';
import AdminPanel from './pages/AdminPanel';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import GhostIntegrationPage from './pages/GhostIntegrationPage';

// Auth Components
import MFAEnrollment from './components/auth/MFAEnrollment';

/**
 * Detect if the app is running in a Wix iframe/embedded context.
 * This is used to route Wix users to the focused Blog Writer experience.
 */
function isWixEnvironment(): boolean {
  if (typeof window === 'undefined') return false;

  // Check URL for Wix indicators
  const url = window.location.href.toLowerCase();
  if (url.includes('wix') || url.includes('wix-dashboard')) return true;

  // Check for Wix SDK in window
  if ('Wix' in window) return true;

  // Check for Wix iframe context
  try {
    if (window.parent !== window && document.referrer.includes('wix')) {
      return true;
    }
  } catch {
    // Cross-origin iframe, might be Wix
  }

  // Check for Wix query parameters
  const params = new URLSearchParams(window.location.search);
  if (params.has('instance') || params.has('compId')) return true;

  return false;
}

/**
 * Wrapper component for MFA Enrollment route.
 * Handles navigation after MFA setup completion or skip.
 */
function MFAEnrollmentWrapper() {
  const navigate = useNavigate();

  // /dashboard is a ProtectedRoute, so sending a Wix merchant there after a
  // successful enrolment would bounce them to /login - re-stranding them at
  // the finish line. Send each identity back where it can actually go, and
  // carry ?instance= through so the Wix dashboard can still authenticate.
  const instanceToken = getWixInstanceFromUrl();
  const returnTo = instanceToken
    ? `/wix-dashboard${window.location.search}`
    : '/dashboard';

  // Whether enrolment is optional right now, or the only way out.
  const challenge = useMfaChallenge();
  const { refreshUser } = useAuth();

  return (
    <MFAEnrollment
      // Re-read the account before leaving. `mfaEnabled` was last read at
      // sign-in, so without this the user lands back on the dashboard still
      // being offered "Enable Two-Factor Auth" for the thing they just
      // enabled - stale until the next sign-in.
      onComplete={() => {
        void refreshUser().finally(() => navigate(returnTo));
      }}
      // Skipping under an active challenge is a trap, not a courtesy: the
      // next request 403s again, the global interceptor re-raises the
      // challenge, and MfaChallengeGuard redirects straight back here. The
      // user sees a redirect flap and has no way out of the one page whose
      // whole purpose is to give them one. Offer skip only when enrolment is
      // genuinely optional.
      onSkip={challenge ? undefined : () => navigate(returnTo)}
    />
  );
}

function App() {
  // Initialize native platform features
  useEffect(() => {
    const initializeNativeFeatures = async () => {
      if (Platform.isNative()) {
        try {
          // Hide splash screen after app loads
          await SplashScreen.hide();

          // Configure status bar
          if (Platform.isIOS()) {
            await StatusBar.setStyle({ style: Style.Dark });
          } else if (Platform.isAndroid()) {
            await StatusBar.setBackgroundColor({ color: '#1a1a2e' });
            await StatusBar.setStyle({ style: Style.Dark });
          }

          // Configure keyboard behavior
          Keyboard.addListener('keyboardWillShow', () => {
            document.body.classList.add('keyboard-open');
          });

          Keyboard.addListener('keyboardWillHide', () => {
            document.body.classList.remove('keyboard-open');
          });
        } catch (error) {
          console.error('Error initializing native features:', error);
        }
      }
    };

    initializeNativeFeatures();

    // Cleanup keyboard listeners
    return () => {
      if (Platform.isNative()) {
        Keyboard.removeAllListeners();
      }
    };
  }, []);

  // Platform detection
  const isNative = Platform.isNative();
  const isWix = useMemo(() => isWixEnvironment(), []);

  // Determine default dashboard based on platform
  // - Native (iOS/Android): Suite experience
  // - Wix: Wix-specific Blog Writer
  // - Web: Suite experience (can also access Wix via /wix-dashboard)
  const DefaultDashboard = isWix ? WixDashboardPage : SuiteDashboardPage;

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
          {/* Public Routes - Skip landing on native */}
          <Route
            path="/"
            element={
              isNative ? <Navigate to="/suite" replace /> : <LandingPage />
            }
          />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/plans" element={<PricingPage />} />
          <Route path="/subscribe" element={<PricingPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/integrations/ghost" element={<GhostIntegrationPage />} />

          {/* Wix Embedded Dashboard - explicit route for Wix context.
              Guarded by MfaChallengeGuard and NOT by ProtectedRoute: Wix
              merchants authenticate with a Wix instance token and have no
              Firebase user, so ProtectedRoute would redirect every one of
              them to /login. The MFA challenge still has to be honoured
              here, which is what the guard does. */}
          <Route
            path="/wix-dashboard"
            element={
              <MfaChallengeGuard>
                <WixDashboardPage />
              </MfaChallengeGuard>
            }
          />

          {/* Suite Dashboard - Full AI Suite for iOS/Android/Web */}
          <Route
            path="/suite"
            element={
              <ProtectedRoute requireEmailVerification={true}>
                <SuiteDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/suite/*"
            element={
              <ProtectedRoute requireEmailVerification={true}>
                <SuiteDashboardPage />
              </ProtectedRoute>
            }
          />

          {/* Default Dashboard - Routes based on platform */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute requireEmailVerification={true}>
                <DefaultDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/*"
            element={
              <ProtectedRoute requireEmailVerification={true}>
                <DefaultDashboard />
              </ProtectedRoute>
            }
          />

          {/* Legacy DashboardPage for backwards compatibility */}
          <Route
            path="/legacy-dashboard"
            element={
              <ProtectedRoute requireEmailVerification={true}>
                <DashboardPage />
              </ProtectedRoute>
            }
          />

          {/* Settings Page */}
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />

          {/* Admin Panel - Protected route */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminPanel />
              </ProtectedRoute>
            }
          />

          {/*
            MFA Setup.

            Guarded by MfaChallengeGuard and NOT by ProtectedRoute, for the
            same reason as /wix-dashboard: ProtectedRoute sends anyone without
            a Firebase user to /login, and a Wix merchant authenticates with a
            Wix instance token and has no Firebase user at all. Wrapping the
            enrolment page in it meant a flagged merchant was redirected here
            by the 403 challenge and then immediately bounced to a login they
            could not use - trapped, with no route to enrolment.

            The backend keeps /api/v1/mfa exempt from enforcement and accepts
            Wix instance tokens, so enrolment itself works for both identities.
          */}
          <Route
            path="/setup-mfa"
            element={
              <MfaChallengeGuard>
                <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50 flex items-center justify-center p-4">
                  <MFAEnrollmentWrapper />
                </div>
              </MfaChallengeGuard>
            }
          />

          {/* Catch-all redirect */}
          <Route
            path="*"
            element={
              <Navigate to={isNative ? '/suite' : '/'} replace />
            }
          />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
